import { zodResolver } from '@hookform/resolvers/zod';
import * as React from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { DistributionPricingPolicyHelpers } from '~/purchase/helpers';
import { advancedExtraBonusDraftSchema } from '~/purchase/schemas';
import {
  IAdvancedExtraBonusDraft,
  IDistributionPricingPolicyBrand,
  IDistributionPricingPolicySeason,
  ISaveAdvancedExtraBonusesPayload,
  IVehicleTypeColumn,
} from '~/purchase/types';

/**
 * The advanced entry is one form: the brands and the sources it targets, the HQ bonus of those brands,
 * and the single bonus being entered. A brand holds one advanced bonus, the matrix being what
 * distinguishes its criteria, so opening the screen on a brand reads its bonus back into the form.
 *
 * Saving and entering another bonus keeps the brands and the sources on screen, since that is what the
 * multiple selection is for: the amounts alone are cleared.
 */
export function useAdvancedExtraBonus({
  seasons,
  vehicleTypes,
  editedBrand,
  onSave,
  onSaved,
}: {
  seasons: IDistributionPricingPolicySeason[];
  vehicleTypes: IVehicleTypeColumn[];
  editedBrand?: IDistributionPricingPolicyBrand | null;
  onSave?: (payload: ISaveAdvancedExtraBonusesPayload) => Promise<unknown>;
  onSaved?: () => void;
}) {
  const methods = useForm<IAdvancedExtraBonusDraft>({
    resolver: zodResolver(advancedExtraBonusDraftSchema),
    defaultValues: DistributionPricingPolicyHelpers.EMPTY_ADVANCED_DRAFT,
  });
  const draft = useWatch({
    control: methods.control,
  }) as IAdvancedExtraBonusDraft;
  const [keepEntering, setKeepEntering] = React.useState(false);

  const { reset } = methods;

  // Every reset names the cells the matrix will show, so the amounts of the previous entry are
  // cleared rather than left on screen — see `withEveryCell`.
  const resetTo = React.useCallback(
    (next: IAdvancedExtraBonusDraft) =>
      reset(
        DistributionPricingPolicyHelpers.withEveryCell(
          next,
          seasons,
          vehicleTypes,
        ),
      ),
    [reset, seasons, vehicleTypes],
  );

  // The brand being edited only changes when the screen is opened from a pencil, or closed.
  React.useEffect(() => {
    resetTo(
      editedBrand
        ? DistributionPricingPolicyHelpers.toAdvancedDraft(editedBrand, seasons)
        : DistributionPricingPolicyHelpers.EMPTY_ADVANCED_DRAFT,
    );
  }, [editedBrand, seasons, resetTo]);

  const clear = () => {
    setKeepEntering(false);
    resetTo(DistributionPricingPolicyHelpers.EMPTY_ADVANCED_DRAFT);
  };

  const submit = async (values: IAdvancedExtraBonusDraft) => {
    if (!onSave) {
      return;
    }

    try {
      await onSave(
        DistributionPricingPolicyHelpers.toSaveAdvancedPayload(
          values,
          seasons,
          vehicleTypes,
        ),
      );
    } catch {
      // A refused save leaves the entry on screen to be corrected: the error itself is reported by
      // the caller's mutation. Swallowing it here also keeps it from escaping `handleSubmit`.
      setKeepEntering(false);

      return;
    }

    if (!keepEntering) {
      // The bonus is stored: the next entry starts from an empty screen rather than from this one.
      clear();
      onSaved?.();

      return;
    }

    setKeepEntering(false);
    resetTo({
      ...DistributionPricingPolicyHelpers.EMPTY_ADVANCED_DRAFT,
      brands: values.brands,
      supplierTypeIds: values.supplierTypeIds,
      hqBonusType: values.hqBonusType,
      hqBonus: values.hqBonus,
    });
  };

  return {
    methods,
    draft,
    submit,
    clear,
    /** Flags the submit that follows as the one keeping the entry open for another bonus. */
    keepEntering: () => setKeepEntering(true),
  };
}
